import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { X, Check, ChevronDown } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Stepper } from "../components/ui/stepper";
import { Textarea } from "../components/ui/textarea";
import { SectionLabel } from "../components/ui/section-label";
import { HeroGlow } from "../components/ui/hero";
import { STATUS_LABELS, PRIORITY_LABELS } from "../components/VacatureBadges";
import { useAuth } from "../hooks/useAuth";
import { updateVacature, listVacaturesByIds, HmpDashboard } from "../api/api";
import { vacatureToSwipeCard } from "../lib/swipe";
import { useHmpDashboard, usePartners, useProfile } from "../hooks/queries";
import { formatSalaryIndication } from "../lib/salary";
import { toast, dismissToast } from "../lib/toast";
import HelpButton from "../components/help/HelpButton";
import { cn } from "@/lib/utils";

const PRIORITIES = ["laag", "normaal", "hoog"] as const;

// Status/priority tiles on the card use the fixed status colour pairs.
const STATUS_TILE: Record<string, string> = {
  open:     "bg-status-ok-bg text-status-ok-fg",
  hold:     "bg-status-warn-bg text-status-warn-fg",
  gesloten: "bg-status-alert-bg text-status-alert-fg",
};
const PRIORITY_TILE: Record<string, string> = {
  hoog:    "bg-status-alert-bg text-status-alert-fg",
  normaal: "bg-status-warn-bg text-status-warn-fg",
  laag:    "bg-status-ok-bg text-status-ok-fg",
};

type Draft = { status: string; priority: string; aantalPosities: number; opmerkingen: string };
type Decision = { kind: "actueel" | "aangepast"; index: number; cardId: number; title: string; status?: string };

function SegmentButton({ active, onClick, children, activeClassName }: { active: boolean; onClick: () => void; children: React.ReactNode; activeClassName: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn("flex-1 rounded-pill p-[13px] text-[15px] font-bold transition-colors", active ? cn("bg-white shadow-soft", activeClassName) : "text-waves-muted hover:text-waves-ink")}
    >
      {children}
    </button>
  );
}

function StatTile({ value, label }: { value: number; label: string }) {
  return (
    <div className="flex flex-1 flex-col gap-1 rounded-card border border-waves-line bg-white p-5 text-left">
      <div className="text-[32px] font-black leading-none tracking-[-0.03em]">{value}</div>
      <div className="text-sm text-waves-muted">{label}</div>
    </div>
  );
}

export default function SwipeReview() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const partnerId = user?.partner_id ?? 0;

  type SwipeCard = HmpDashboard["stale_vacatures"][number];
  // secondaryIds: the extra batch offered once these cards are done.
  type RouteState = { cards?: SwipeCard[]; title?: string; secondaryIds?: number[] } | null;
  const routeCards = (location.state as RouteState)?.cards;
  const routeTitle = (location.state as RouteState)?.title;
  const routeSecondaryIds = (location.state as RouteState)?.secondaryIds;

  const { data: dashboard, isLoading: loading } = useHmpDashboard(partnerId);
  const { data: partners } = usePartners();
  const { data: profile } = useProfile();
  const kamName = profile?.waves_contact?.name?.split(" ")[0] ?? "je accountmanager";

  const [current, setCurrent] = useState(0);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [decisions, setDecisions] = useState<Decision[]>([]);
  const [panelOpen, setPanelOpen] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [textOpen, setTextOpen] = useState(false);

  const cardRef = useRef<HTMLDivElement>(null);
  const leftStripRef = useRef<HTMLDivElement>(null);
  const rightStripRef = useRef<HTMLDivElement>(null);
  const stampRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);
  const startXRef = useRef(0);
  const dxRef = useRef(0);
  const flyingRef = useRef(false);
  // Only the most recent decision can be undone, so only its toast is kept on screen.
  const toastIdRef = useRef<string | null>(null);
  const undoRef = useRef<() => void>(() => {});

  // Once the HMP accepts the extra batch, it replaces the primary cards.
  const [extraCards, setExtraCards] = useState<SwipeCard[] | null>(null);
  const [loadingExtra, setLoadingExtra] = useState(false);
  const cards = extraCards ?? routeCards ?? dashboard?.stale_vacatures ?? [];
  // The extra batch is offered once, after the primary one.
  const secondaryIds = extraCards ? [] : routeCards ? routeSecondaryIds ?? [] : dashboard?.secondary_vacature_ids ?? [];
  const total = cards.length;
  const partner = (partners ?? []).find(p => p.id === partnerId);

  const dismissCurrentToast = useCallback(() => {
    if (toastIdRef.current) dismissToast(toastIdRef.current);
    toastIdRef.current = null;
  }, []);

  useEffect(() => () => dismissCurrentToast(), [dismissCurrentToast]);
  useEffect(() => { setTextOpen(false); }, [current]);

  const armToast = useCallback((text: string) => {
    dismissCurrentToast();
    toastIdRef.current = toast(text, {
      kind: "success",
      action: { label: "Ongedaan maken", onClick: () => undoRef.current() },
    });
  }, [dismissCurrentToast]);

  const paint = useCallback((dx: number, instant: boolean) => {
    const t = Math.min(Math.abs(dx) / 150, 1);
    const l = leftStripRef.current, r = rightStripRef.current, s = stampRef.current;
    const trans = instant ? "none" : "width .25s ease, opacity .2s ease";
    if (l) { l.style.transition = trans; l.style.width = (dx < 0 ? t * 52 : 0) + "px"; }
    if (r) { r.style.transition = trans; r.style.width = (dx > 0 ? t * 52 : 0) + "px"; }
    if (s) {
      s.style.transition = trans;
      if (Math.abs(dx) < 24) s.style.opacity = "0";
      else {
        s.style.opacity = String(t);
        s.textContent = dx > 0 ? "✓ Nog actueel" : "Niet actueel ←";
        s.style.background = dx > 0 ? "#C5E8FA" : "#FFE5DD";
        s.style.color = dx > 0 ? "#3DB8F5" : "#E8401F";
      }
    }
  }, []);

  const resetCard = useCallback((instant: boolean) => {
    const el = cardRef.current;
    if (el) {
      el.style.transition = instant ? "none" : "transform .3s cubic-bezier(.22,1,.36,1)";
      el.style.transform = "";
      el.style.opacity = "1";
    }
    paint(0, instant);
  }, [paint]);

  const commitActueel = useCallback(async () => {
    const card = cards[current];
    if (!card) return;
    setBusy(true);
    try { await updateVacature(card.id, { reviewed_at: new Date().toISOString() }); } catch { /* advance anyway */ }
    setBusy(false);
    setDecisions(d => [...d, { kind: "actueel", index: current, cardId: card.id, title: card.functie }]);
    armToast(`${card.functie} blijft open`);
    if (current + 1 >= total) setDone(true);
    else setCurrent(c => c + 1);
  }, [cards, current, total, armToast]);

  const openPanel = useCallback(() => {
    const card = cards[current];
    if (!card) return;
    setDraft({
      status: card.status,
      priority: card.priority,
      aantalPosities: card.aantal_posities,
      opmerkingen: card.opmerkingen ?? "",
    });
    setPanelOpen(true);
  }, [cards, current]);

  const flyOut = useCallback((dir: 1 | -1) => {
    if (busy || panelOpen) return;
    if (dir < 0) {
      resetCard(false);
      openPanel();
      return;
    }
    if (flyingRef.current) return;
    flyingRef.current = true;
    const el = cardRef.current;
    if (el) {
      el.style.transition = "transform .3s ease-out, opacity .3s ease-out";
      el.style.transform = `translateX(${dir * 1000}px) rotate(${dir * 16}deg)`;
      el.style.opacity = "0";
    }
    paint(dir * 200, false);
    setTimeout(() => {
      flyingRef.current = false;
      commitActueel();
      requestAnimationFrame(() => resetCard(true));
    }, 270);
  }, [busy, panelOpen, resetCard, openPanel, paint, commitActueel]);

  const savePanel = useCallback(async () => {
    const card = cards[current];
    if (!card || !draft) return;
    setBusy(true);
    try {
      await updateVacature(card.id, {
        status: draft.status,
        priority: draft.priority,
        aantal_posities: draft.aantalPosities,
        opmerkingen: draft.opmerkingen,
        reviewed_at: new Date().toISOString(),
      });
    } catch { /* advance anyway */ }
    setBusy(false);
    setDecisions(d => [...d, { kind: "aangepast", index: current, cardId: card.id, title: card.functie, status: draft.status }]);
    armToast(draft.status === "gesloten" ? `${card.functie} gesloten` : `${card.functie} bijgewerkt`);
    setPanelOpen(false);
    setDraft(null);
    if (current + 1 >= total) setDone(true);
    else setCurrent(c => c + 1);
    requestAnimationFrame(() => resetCard(true));
  }, [cards, current, draft, total, armToast, resetCard]);

  const cancelPanel = useCallback(() => {
    setPanelOpen(false);
    setDraft(null);
    requestAnimationFrame(() => resetCard(true));
  }, [resetCard]);

  const handleUndo = useCallback(() => {
    setDecisions(prev => {
      const last = prev[prev.length - 1];
      if (!last) return prev;
      dismissCurrentToast();
      if (last.kind === "aangepast") {
        const original = cards[last.index];
        if (original) {
          updateVacature(original.id, {
            status: original.status,
            priority: original.priority,
            aantal_posities: original.aantal_posities,
            opmerkingen: original.opmerkingen ?? "",
          }).catch(() => { /* best-effort revert */ });
        }
      }
      setDone(false);
      setCurrent(last.index);
      requestAnimationFrame(() => resetCard(true));
      return prev.slice(0, -1);
    });
  }, [cards, resetCard]);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    if (busy || panelOpen) return;
    const target = e.target as HTMLElement;
    if (target.closest && target.closest("button, textarea, input")) return;
    draggingRef.current = true;
    startXRef.current = e.clientX;
    dxRef.current = 0;
    const el = cardRef.current;
    if (el) { el.style.transition = "none"; el.style.cursor = "grabbing"; }
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ignore */ }
  }, [busy, panelOpen]);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!draggingRef.current) return;
    const dx = e.clientX - startXRef.current;
    dxRef.current = dx;
    const el = cardRef.current;
    if (el) el.style.transform = `translateX(${dx}px) rotate(${dx / 50}deg)`;
    paint(dx, true);
  }, [paint]);

  const onPointerUp = useCallback(() => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    const el = cardRef.current;
    if (el) el.style.cursor = "grab";
    const dx = dxRef.current;
    dxRef.current = 0;
    if (dx > 140) flyOut(1);
    else if (dx < -140) flyOut(-1);
    else resetCard(false);
  }, [flyOut, resetCard]);

  const restartCheck = useCallback(() => {
    setDecisions([]);
    setCurrent(0);
    setDone(false);
    dismissCurrentToast();
    requestAnimationFrame(() => resetCard(true));
  }, [resetCard]);

  async function startExtra() {
    setLoadingExtra(true);
    try {
      const { data } = await listVacaturesByIds(secondaryIds);
      setExtraCards(data.map(vacatureToSwipeCard));
      restartCheck();
    } catch { /* handled by the global error snackbar */ }
    finally { setLoadingExtra(false); }
  }

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (panelOpen) return;
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if (e.key === "ArrowRight") { e.preventDefault(); flyOut(1); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); flyOut(-1); }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [flyOut, panelOpen]);

  undoRef.current = handleUndo;

  if (loading && !routeCards) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-waves-subtle text-sm text-waves-muted">
        Laden…
      </div>
    );
  }

  const emptyOrDone = (!loading && total === 0) || done;

  if (emptyOrDone) {
    const countActueel = decisions.filter(d => d.kind === "actueel").length;
    const countAangepast = decisions.filter(d => d.kind === "aangepast" && d.status !== "gesloten").length;
    const countGesloten = decisions.filter(d => d.status === "gesloten").length;
    return (
      <div className="relative flex h-screen w-screen flex-col items-center justify-center overflow-hidden bg-gradient-to-b from-white to-waves-subtle px-6 text-waves-ink">
        <HeroGlow />
        {done ? (
          <div className="relative z-[1] flex max-w-xl flex-col items-center gap-6 text-center">
            <SectionLabel>Klaar</SectionLabel>
            <h1 className="text-[36px] font-black leading-[1.06] tracking-[-0.025em] md:text-[44px]">
              Alle {total} beoordeeld.<br />{profile?.waves_contact ? kamName : "Waves"} <span className="text-waves-accent">is weer helemaal bij</span>.
            </h1>
            <div className="flex w-full gap-3">
              <StatTile value={countActueel} label="blijven open" />
              <StatTile value={countAangepast} label="aangepast" />
              <StatTile value={countGesloten} label="gesloten" />
            </div>
            {secondaryIds.length > 0 ? (
              <>
                <p className="text-[15px] leading-relaxed text-waves-body text-pretty">
                  Er {secondaryIds.length === 1 ? "is nog 1 vacature" : `zijn nog ${secondaryIds.length} vacatures`} die je ook kunt bijwerken.
                </p>
                <div className="flex flex-wrap justify-center gap-3">
                  <Button size="lg" disabled={loadingExtra} onClick={startExtra}>{loadingExtra ? "Laden…" : "Ook bijwerken"}</Button>
                  <Button size="lg" variant="outline" className="border-waves-ink" onClick={() => navigate("/")}>Naar dashboard</Button>
                </div>
              </>
            ) : (
              <div className="flex flex-wrap justify-center gap-3">
                <Button size="lg" onClick={() => navigate("/")}>Naar dashboard</Button>
                <Button size="lg" variant="outline" className="border-waves-ink" onClick={restartCheck}>Opnieuw doorlopen</Button>
              </div>
            )}
          </div>
        ) : secondaryIds.length > 0 ? (
          <div className="relative z-[1] flex max-w-sm flex-col items-center gap-4 text-center">
            <h1 className="text-3xl font-black tracking-[-0.02em]">Bijna <span className="text-waves-accent">actueel</span>.</h1>
            <p className="text-[15px] leading-relaxed text-waves-body">
              Er {secondaryIds.length === 1 ? "is nog 1 vacature" : `zijn nog ${secondaryIds.length} vacatures`} die je kunt bijwerken.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Button size="lg" disabled={loadingExtra} onClick={startExtra}>{loadingExtra ? "Laden…" : "Bijwerken"}</Button>
              <Button size="lg" variant="outline" className="border-waves-ink" onClick={() => navigate("/")}>Terug naar dashboard</Button>
            </div>
          </div>
        ) : (
          <div className="relative z-[1] flex max-w-sm flex-col items-center gap-4 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-pill bg-status-ok-bg text-status-ok-fg">
              <Check size={26} />
            </div>
            <h1 className="text-3xl font-black tracking-[-0.02em]">Alles is <span className="text-waves-accent">actueel</span>.</h1>
            <p className="text-[15px] leading-relaxed text-waves-body">Er zijn geen vacatures die een check nodig hebben.</p>
            <Button size="lg" onClick={() => navigate("/")}>Terug naar dashboard</Button>
          </div>
        )}
      </div>
    );
  }

  const card = cards[current];
  const displayDays = card.days_since_update;
  const spotsLabel = card.aantal_posities === 1 ? "plek" : "plekken";
  const statusLabel = STATUS_LABELS[card.status] ?? card.status;
  const prioLabel = PRIORITY_LABELS[card.priority] ?? card.priority;

  return (
    <div className="relative flex h-screen w-screen flex-col overflow-hidden bg-waves-subtle text-waves-ink">
      {/* Top bar */}
      <div className="mx-auto flex w-full max-w-[1128px] items-center justify-between gap-6 px-4 py-6 md:px-14">
        <Button variant="outline" onClick={() => navigate("/")} className="shrink-0">
          <X /> <span className="hidden sm:inline">Terug naar dashboard</span>
        </Button>
        <div data-tour="review-progress" className="flex max-w-[440px] flex-1 flex-col gap-2">
          <div className="flex justify-between gap-3 text-[13px] text-waves-muted">
            <span>
              Vacature <b className="font-mono font-semibold text-waves-ink">{current + 1}</b> van{" "}
              <b className="font-mono font-semibold text-waves-ink">{total}</b>
            </span>
            <span className="truncate">{(extraCards ? "Extra vacatures" : routeTitle) ?? `${decisions.length} beoordeeld · alles opgeslagen`}</span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-pill bg-waves-line">
            <div className="h-full rounded-pill bg-waves-zee transition-[width] duration-300" style={{ width: `${Math.round((decisions.length / total) * 100)}%` }} />
          </div>
        </div>
        <div className="flex shrink-0 justify-end md:w-[104px]"><HelpButton /></div>
      </div>

      {/* Card area */}
      <div className="flex flex-1 items-center justify-center overflow-y-auto px-4 pb-6">
        <div className="flex w-full max-w-[760px] flex-col gap-4">
          <div className="flex justify-between px-2">
            <span className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-label text-waves-muted">
              <span className="flex h-[26px] w-[26px] items-center justify-center rounded-pill border border-waves-line bg-waves-mist text-[13px] text-waves-body">←</span>
              niet actueel
            </span>
            <span className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-label text-waves-muted">
              nog actueel
              <span className="flex h-[26px] w-[26px] items-center justify-center rounded-pill bg-waves-lucht text-[13px] text-waves-zee">→</span>
            </span>
          </div>

          <div className="relative">
            {current + 2 < total && <div className="absolute inset-x-7 top-[22px] h-full rounded-card-lg border border-waves-line bg-waves-mist" />}
            {current + 1 < total && <div className="absolute inset-x-3.5 top-[11px] h-full rounded-card-lg border border-waves-line bg-white" />}

            {/* Active card */}
            <div
              ref={cardRef}
              data-tour="review-card"
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
              className="relative flex select-none overflow-hidden rounded-card-lg border border-waves-line bg-white"
              style={{ boxShadow: "0 1px 2px rgba(16,32,47,0.04), 0 8px 20px rgba(16,32,47,0.05), 0 28px 60px rgba(16,32,47,0.10)", cursor: "grab", touchAction: "pan-y" }}
            >
              <div ref={leftStripRef} className="shrink-0 bg-waves-koraal" style={{ width: 0 }} />
              <div className="relative flex min-w-0 flex-1 flex-col">
                <HeroGlow className="-bottom-[52%] h-[88%]" />
                <div className="relative z-[1] flex flex-wrap items-center justify-between gap-4 border-b border-waves-line p-6">
                  <div className="flex min-w-0 items-center gap-4">
                    {partner?.logo_url
                      ? <img src={partner.logo_url} alt={partner.name} className="block h-10 w-auto shrink-0" draggable={false} />
                      : partner && <span className="text-[15px] font-black">{partner.name}</span>}
                    {card.locatie && <div className="truncate text-sm text-waves-muted">{card.locatie}</div>}
                  </div>
                  <div className="flex shrink-0 items-stretch gap-6">
                    <div className="flex flex-col gap-2">
                      <SectionLabel>Niet bijgewerkt</SectionLabel>
                      <div className="font-mono text-lg font-semibold leading-none">{displayDays} {displayDays === 1 ? "dag" : "dagen"}</div>
                    </div>
                  </div>
                </div>

                <div className="relative z-[1] flex flex-col gap-6 p-6">
                  <h1 className="text-[32px] font-black leading-[1.04] tracking-[-0.025em] md:text-[44px]">{card.functie}</h1>
                  <div className="inline-flex flex-col gap-4 self-start">
                    <div className="flex flex-wrap gap-4">
                      <div className={cn("flex flex-col gap-2 rounded-[14px] px-6 py-4", STATUS_TILE[card.status] ?? STATUS_TILE.open)}>
                        <div className="text-[11px] font-bold uppercase tracking-label">Status</div>
                        <div className="text-2xl font-black leading-none tracking-[-0.02em]">{statusLabel}</div>
                      </div>
                      <div className={cn("flex flex-col gap-2 rounded-[14px] px-6 py-4", PRIORITY_TILE[card.priority] ?? PRIORITY_TILE.normaal)}>
                        <div className="text-[11px] font-bold uppercase tracking-label">Prioriteit</div>
                        <div className="text-2xl font-black leading-none tracking-[-0.02em]">{prioLabel}</div>
                      </div>
                    </div>
                    <div className="rounded-[14px] bg-waves-mist px-6 py-3.5 text-[15px] font-bold leading-tight">
                      {displayDays} {displayDays === 1 ? "dag" : "dagen"} niet bijgewerkt.
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-4 border-t border-waves-line pt-6">
                    <div className="font-mono text-[13px] font-medium text-waves-body">
                      {card.aantal_posities} {spotsLabel} · {formatSalaryIndication(card)}
                      {card.contactpersoon && <> · {card.contactpersoon}</>}
                    </div>
                    {card.opmerkingen && (
                      <button type="button" onClick={() => setTextOpen(o => !o)} className="inline-flex items-center gap-2 text-sm font-bold hover:text-waves-accent">
                        {textOpen ? "Verberg omschrijving" : "Bekijk omschrijving"}
                        <ChevronDown size={16} className={cn("transition-transform", textOpen && "rotate-180")} />
                      </button>
                    )}
                  </div>
                  {textOpen && card.opmerkingen && (
                    <div className="-mt-2 flex flex-col gap-4 rounded-card border border-waves-line bg-white p-6">
                      <div className="whitespace-pre-line text-[15px] leading-[1.65] text-waves-body">{card.opmerkingen}</div>
                      <div className="text-[13px] text-waves-muted">Klopt dit niet meer? Swipe naar links.</div>
                    </div>
                  )}
                </div>

                <div className="relative z-[1] flex gap-3 border-t border-waves-line p-6 sm:gap-4">
                  <button
                    onClick={() => flyOut(-1)}
                    disabled={busy}
                    className="flex flex-1 items-center justify-center gap-2 rounded-pill bg-waves-koraal p-4 text-base font-bold text-white transition-colors hover:bg-waves-koraal-hover disabled:opacity-60"
                  >
                    ← Niet actueel
                  </button>
                  <button
                    onClick={() => flyOut(1)}
                    disabled={busy}
                    className="flex flex-1 items-center justify-center gap-2 rounded-pill bg-waves-ink p-4 text-base font-bold text-white transition-colors hover:bg-waves-zee disabled:opacity-60"
                  >
                    Nog actueel →
                  </button>
                </div>
              </div>
              <div ref={rightStripRef} className="shrink-0 bg-waves-zee" style={{ width: 0 }} />
              <div
                ref={stampRef}
                className="pointer-events-none absolute left-1/2 top-6 z-[2] -translate-x-1/2 whitespace-nowrap rounded-pill px-5 py-2.5 text-[15px] font-bold"
                style={{ opacity: 0 }}
              />
            </div>
          </div>

          <div className="relative z-[2] hidden flex-col items-center gap-2 pt-6 sm:flex">
            <div className="text-sm text-waves-body">Pak de kaart en swipe, of gebruik de knoppen</div>
            <div className="flex items-center gap-2 text-[13px] text-waves-muted">
              <kbd className="rounded-lg border border-waves-line px-2 py-0.5 font-mono font-semibold text-waves-ink">←</kbd>
              <kbd className="rounded-lg border border-waves-line px-2 py-0.5 font-mono font-semibold text-waves-ink">→</kbd>
              <span>de pijltjestoetsen werken ook</span>
            </div>
          </div>
        </div>
      </div>

      {/* "Niet actueel" panel */}
      <Dialog open={panelOpen} onOpenChange={open => { if (!open) cancelPanel(); }}>
        <DialogContent data-tour="review-panel" className="max-h-[calc(100dvh-2rem)] max-w-[800px] gap-0 overflow-auto rounded-[28px] p-0">
          <div className="flex flex-col gap-2 border-b border-waves-line px-6 pb-6 pt-10 pr-20 md:px-11">
            <SectionLabel>Niet meer actueel</SectionLabel>
            <DialogTitle className="text-[30px]">{card.functie}</DialogTitle>
            <DialogDescription className="text-sm text-waves-muted">{card.locatie || "Pas aan wat er veranderd is"}</DialogDescription>
          </div>
          {draft && (
            <div className="grid grid-cols-1 gap-6 px-6 py-6 md:grid-cols-2 md:px-11">
              <div className="flex flex-col gap-4">
                <SectionLabel>Status vacature</SectionLabel>
                <div className="flex gap-2 rounded-pill bg-waves-mist p-1">
                  <SegmentButton active={draft.status === "open"} onClick={() => setDraft(d => d && { ...d, status: "open" })} activeClassName="text-status-ok-fg">Open</SegmentButton>
                  <SegmentButton active={draft.status === "gesloten"} onClick={() => setDraft(d => d && { ...d, status: "gesloten" })} activeClassName="text-status-alert-fg">Gesloten</SegmentButton>
                </div>
              </div>
              <div className="flex flex-col gap-4">
                <SectionLabel>Aantal plekken</SectionLabel>
                <Stepper aria-label="Aantal plekken" value={draft.aantalPosities} min={0} onChange={n => setDraft(d => d && { ...d, aantalPosities: n })} />
              </div>
              <div className="flex flex-col gap-4 md:col-span-2">
                <SectionLabel>Prioriteit</SectionLabel>
                <div className="flex gap-2">
                  {PRIORITIES.map(p => (
                    <button
                      key={p}
                      type="button"
                      aria-pressed={draft.priority === p}
                      onClick={() => setDraft(d => d && { ...d, priority: p })}
                      className={cn(
                        "flex-1 rounded-pill border p-3.5 text-[15px] font-bold transition-colors",
                        draft.priority === p ? cn("border-transparent", PRIORITY_TILE[p]) : "border-waves-line bg-white text-waves-body hover:bg-waves-mist",
                      )}
                    >
                      {PRIORITY_LABELS[p]}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex flex-col gap-4 md:col-span-2">
                <div className="flex items-baseline gap-2">
                  <SectionLabel>Toelichting voor {kamName}</SectionLabel>
                  <span className="text-xs text-waves-muted">optioneel</span>
                </div>
                <Textarea
                  value={draft.opmerkingen}
                  onChange={e => setDraft(d => d && { ...d, opmerkingen: e.target.value })}
                  placeholder="Bijvoorbeeld: één plek is intern ingevuld, de andere twee lopen door."
                  className="h-24 resize-none"
                />
              </div>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-4 border-t border-waves-line px-6 py-6 md:px-11">
            <Button size="lg" onClick={savePanel} disabled={busy}>Opslaan en volgende</Button>
            <Button size="lg" variant="outline" onClick={cancelPanel}>← Terug naar de kaart</Button>
            <p className="text-[13px] text-waves-muted">{profile?.waves_contact ? kamName : "Waves"} ziet je wijziging direct nadat je opslaat.</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
